import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle, Circle, Share2, ArrowLeft } from "lucide-react";
import { predefinedChecklists } from "../../data/checklistsData";
import Meta from "../../components/Meta";
import Footer from "../../components/Footer";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ChecklistNotFound from "./ChecklistNotFound";
import Navbar from "../../components/Navbar";

const ChecklistPage = () => {
  const { slug } = useParams();
  const [checklist, setChecklist] = useState(null);
  const [checklistProgress, setChecklistProgress] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      const allChecklists = [...predefinedChecklists];
      const currentChecklist = allChecklists.find((c) => c.slug === slug);
      setChecklist(currentChecklist);

      if (currentChecklist) {
        const saved = localStorage.getItem(
          `checklist-progress-${currentChecklist.id}`
        );
        if (saved) {
          setChecklistProgress(JSON.parse(saved));
        }
      }
      setLoading(false);
    }, 500); // Simulate network delay

    return () => clearTimeout(timer);
  }, [slug]);

  const toggleItem = (checklistId, itemIndex) => {
    setChecklistProgress((prev) => {
      const newProgress = {
        ...prev,
        [itemIndex]: !prev[itemIndex],
      };
      localStorage.setItem(
        `checklist-progress-${checklistId}`,
        JSON.stringify(newProgress)
      );
      return newProgress;
    });
  };

  const getProgress = (checklistId, itemsCount) => {
    const completed = Object.values(checklistProgress || {}).filter(
      Boolean
    ).length;
    return Math.round((completed / itemsCount) * 100);
  };

  const shareChecklist = (checklistId) => {
    const url = `${window.location.origin}/checklists/${checklistId}`;
    navigator.clipboard.writeText(url);
    toast.success("Checklist link copied to clipboard!", {
      position: "bottom-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 border-4 border-blue-500 rounded-full border-t-transparent animate-spin"></div>
          <p className="mt-4 text-lg text-gray-700">Loading checklist...</p>
        </div>
      </div>
    );
  }

  if (!checklist) {
    return <ChecklistNotFound />;
  }

  const progress = getProgress(checklist.id, checklist.items.length);
  const completedItems = Object.values(checklistProgress || {}).filter(
    Boolean
  ).length;

  return (
    <>
      <Meta
        title={`${checklist.name} - Govind Sinha & Co.`}
        description={`Document checklist for ${checklist.name}`}
        canonical={`https://cagovindsinha.com/checklists/${slug}`}
      />
      <div className="min-h-screen pt-16 bg-gradient-to-br from-blue-50 to-indigo-100">
        <Navbar />
        <div className="container px-4 py-8 mx-auto max-w-7xl">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <Link
                to="/checklists"
                className="p-2 text-gray-600 rounded-full hover:text-blue-600 hover:bg-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-gray-900">
                  {checklist.name}
                </h1>
                <p className="text-gray-600 capitalize">
                  {checklist.category} • {checklist.items.length} items
                </p>
              </div>
              <button
                onClick={() => shareChecklist(checklist.id)}
                className="flex items-center gap-2 px-4 py-2 text-blue-600 transition-colors border border-blue-600 rounded-lg hover:bg-blue-50"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>

            {/* Progress Card */}
            <div className="p-6 mb-6 bg-white rounded-lg shadow-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Progress
                </h3>
                <span className="text-2xl font-bold text-blue-600">
                  {progress}%
                </span>
              </div>
              <div className="w-full h-3 mb-2 bg-gray-200 rounded-full">
                <div
                  className="h-3 transition-all duration-300 bg-blue-600 rounded-full"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600">
                {completedItems} of {checklist.items.length} items completed
              </p>
            </div>

            {/* Checklist Items */}
            <div className="p-6 bg-white rounded-lg shadow-md">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                Documents Required
              </h3>
              <div className="space-y-3">
                {checklist.items.map((item, index) => {
                  const isCompleted = checklistProgress[index] || false;
                  return (
                    <div
                      key={index}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        isCompleted
                          ? "bg-green-50 border-green-200 text-green-800"
                          : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
                      }`}
                      onClick={() => toggleItem(checklist.id, index)}
                    >
                      {isCompleted ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <Circle className="w-5 h-5 text-gray-400" />
                      )}
                      <span
                        className={`flex-1 ${
                          isCompleted ? "line-through" : ""
                        }`}
                      >
                        {item}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <ToastContainer />
    </>
  );
};

export default ChecklistPage;
